
export interface QuoteSource {
  title: string;
  href: string;
}

export type TextBlock =
  | { type: 'text'; value: string }
  | { type: 'quote'; text: string; source: QuoteSource | null };

const SOURCE_RE = /^Инструкция\s+«(.+?)»:\s+(\S+)$/;

export function toInstructionPath(url: string): string | null {
  if (url.startsWith('/instructions/')) return url;
  try {
    const parsed = new URL(url);
    if (parsed.origin !== window.location.origin) return null;
    if (!parsed.pathname.startsWith('/instructions/')) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function safeDecodeURIComponent(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

export function instructionUrlId(href: string): string | null {
  const match = /^\/instructions\/([^/?#]+)/.exec(href);
  return match ? safeDecodeURIComponent(match[1]) : null;
}

function parseSourceLine(line: string): QuoteSource | null {
  const match = SOURCE_RE.exec(line.trim());
  if (!match) return null;
  const href = toInstructionPath(match[2]);
  return href ? { title: match[1], href } : null;
}

export function parseTextBlocks(text: string): TextBlock[] {
  const lines = text.split('\n');
  const blocks: TextBlock[] = [];
  let plain: string[] = [];

  const flushPlain = (): void => {
    if (plain.length === 0) return;
    const value = plain.join('\n');
    if (value.trim()) blocks.push({ type: 'text', value: value.replace(/^\n+|\n+$/g, '') });
    plain = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (line.startsWith('>')) {
      const quoted: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        quoted.push(lines[i].replace(/^>\s?/, ''));
        i += 1;
      }
      const source = i < lines.length ? parseSourceLine(lines[i]) : null;
      if (!source) i -= 1;

      flushPlain();
      blocks.push({ type: 'quote', text: quoted.join('\n').trim(), source });
      continue;
    }

    const orphanSource = parseSourceLine(line);
    if (orphanSource) {
      flushPlain();
      blocks.push({ type: 'quote', text: '', source: orphanSource });
      continue;
    }

    plain.push(line);
  }

  flushPlain();
  return blocks;
}

export function hasRichBlocks(text: string): boolean {
  return parseTextBlocks(text).some((b) => b.type !== 'text');
}
